const destinations = [
  {
    icon: "ri-ship-line",
    title: "The Bahamas & Caribbean",
    description:
      "Caribbean cruises from Tampa — Lakewood Ranch's closest major cruise port at roughly 45 minutes north — are enormously popular among families, retirees, and golf groups. Western Caribbean itineraries hit Cozumel, Grand Cayman, and Jamaica; Eastern Caribbean reaches St. Thomas, San Juan, and the Bahamas; Southern Caribbean extends to Aruba, Curacao, and Barbados. Lakewood Ranch residents also enjoy easy access to Port Everglades and the Port of Miami for broader Caribbean options. The six-month validity rule applies to all cruise passengers, and the community's high volume of family and group cruise bookings — many organized through country club and school travel groups — means we see frequent passport emergencies from this destination category.",
    tags: ["Cruises", "Tampa Port", "Family Travel"],
  },
  {
    icon: "ri-map-pin-2-line",
    title: "Argentina, UK & International Polo",
    description:
      "Lakewood Ranch's deep polo culture generates unique travel demand. Buenos Aires and the Argentine pampas host the world's premier polo season from November through April — the inverse of Florida's polo calendar. Many Lakewood Ranch families and players fly to Argentina for winter training, tournaments, and the iconic Argentine Open at Palermo. The UK polo season runs from May through September in Cirencester, Windsor, and Sussex. Wellington, Florida — the global polo capital — sits just two hours southeast and draws Lakewood Ranch spectators and players year-round. These trips require careful passport timing: Argentina requires six months validity, the UK has strict entry requirements, and the international polo circuit means frequent border crossings.",
    tags: ["Polo", "Argentina", "UK", "Sporting Travel"],
  },
  {
    icon: "ri-landscape-line",
    title: "Europe",
    description:
      "European destinations draw Lakewood Ranch's affluent, educated demographic for river cruises along the Rhine and Danube, cultural tours to Paris, Rome, and Barcelona, school-organized educational trips to London and Berlin, and Mediterranean yacht charters during summer break. The Schengen three-month validity rule catches many travelers who assume their passport is fine because it has not technically expired. Lakewood Ranch's international school trips, polo family European tours, and golf buddy European trips all create predictable demand for passport services. Many residents maintain second homes or extended family connections in Italy, Germany, and the UK that drive recurring European travel.",
    tags: ["River Cruises", "School Trips", "Cultural Travel"],
  },
  {
    icon: "ri-treasure-map-line",
    title: "Scotland, Ireland & Golf Destinations",
    description:
      "The UK and Ireland are pilgrimage destinations for Lakewood Ranch golfers. St. Andrews in Scotland — the home of golf — draws members from the Lakewood Ranch Golf and Country Club for bucket-list trips organized through the club's travel committee. Ireland's championship links courses — Ballybunion, Lahinch, Royal County Down, Old Head — attract golf buddy groups every summer. Portugal's Algarve, Spain's Costa del Sol, and the French Riviera also draw golf travelers from the community. The six-month validity requirement is strict for UK entry, and we see passport emergencies from golf groups every season when one member discovers their passport expired since the last trip. These group bookings are typically non-refundable and fixed-date.",
    tags: ["Golf", "Scotland", "Ireland", "Group Travel"],
  },
  {
    icon: "ri-sun-line",
    title: "Mexico & Costa Rica",
    description:
      "Cancun, Riviera Maya, Puerto Vallarta, and Cabo San Lucas are top Mexican destinations for Lakewood Ranch families, spring break travelers, and couples. Tampa International offers direct flights to Cancun and Cozumel that are convenient for residents. Costa Rica's eco-tourism — zip-lining, volcano hiking, rainforest canopy tours — appeals to active families and adventure-seeking teenagers. The Mexico six-month validity rule applies to all passport holders, and the child passport trap hits particularly hard during spring break and winter break when families book trips months in advance but forget to check their children's document expiration dates. All-inclusive resort packages are typically non-refundable within 30–60 days of departure.",
    tags: ["All-Inclusive", "Eco-Tourism", "Spring Break"],
  },
  {
    icon: "ri-compass-3-line",
    title: "Panama Canal & Central America",
    description:
      "Panama Canal full-transit cruises, Costa Rica eco-tours, and Belize diving trips appeal to Lakewood Ranch's active retiree and adventure-travel community. Panama Canal cruises are a bucket-list item for many affluent residents, booking 12–18 months in advance through major cruise lines departing from Tampa, Fort Lauderdale, and Miami. Belize's barrier reef — the second largest in the world — draws diving enthusiasts. Costa Rica's Arenal Volcano, Monteverde cloud forest, and Manuel Antonio National Park attract families with teenagers seeking adventure beyond the beach. These trips often book well in advance through country club travel groups, creating a long window during which passports can expire unnoticed. The multi-country nature of Central American itineraries also means multiple entry requirements to track.",
    tags: ["Bucket List", "Diving", "Cruises"],
  },
];

const stats = [
  { value: "55%", label: "Caribbean cruise or European family trip requests" },
  { value: "SRQ", label: "Primary airport — 20–25 min from Lakewood Ranch" },
  { value: "Nov–Apr", label: "Peak polo, golf & family travel season" },
  { value: "24 hr", label: "Shortest emergency turnaround handled" },
];

export default function LakewoodRanchDestinationsSection() {
  return (
    <section id="destinations" className="py-16 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-[#B22222] text-xs font-bold uppercase tracking-[0.15em] mb-3">
            Popular Destinations
          </span>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Where Lakewood Ranch Residents Travel
          </h2>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            From Caribbean cruises out of Tampa to Argentine polo tournaments, Scottish golf pilgrimages, and European school trips — here is where Lakewood Ranch travelers are heading.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {destinations.map((d, i) => (
            <div
              key={i}
              className="bg-[#F8F9FB] rounded-lg p-6 border border-gray-100 hover:border-[#F6C344]/50 transition-all hover:shadow-md"
            >
              <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center mb-4">
                <i className={`${d.icon} text-[#F6C344] text-lg`}></i>
              </div>
              <h3 className="font-serif text-base font-bold text-[#1A365D] mb-3">{d.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">{d.description}</p>
              <div className="flex flex-wrap gap-2">
                {d.tags.map((tag, j) => (
                  <span
                    key={j}
                    className="inline-block bg-white text-[#1A365D] text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border border-gray-200"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Stats bar */}
        <div className="bg-[#1A365D] rounded-lg p-6 md:p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((s, i) => (
              <div key={i}>
                <div className="text-[#F6C344] font-bold text-2xl md:text-3xl mb-1">{s.value}</div>
                <div className="text-white/70 text-xs">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}